import { useEffect, useState } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { Shield, LogOut, KeyRound, X, Eye, EyeOff, Building2, Database, CreditCard, TrendingUp, LifeBuoy } from "lucide-react";
import { ClockInReminderToggle } from "@/components/ClockInReminderToggle";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function SuperAdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [pwOpen, setPwOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    useEffect(() => {
        const fetchUnread = () => api.get("/super-admin/support", { params: { status: "all" } }).then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
        fetchUnread();
        const t = setInterval(fetchUnread, 60000);
        window.addEventListener("sa-support-read", fetchUnread);
        return () => { clearInterval(t); window.removeEventListener("sa-support-read", fetchUnread); };
    }, [location.pathname]);

    const onLogout = async () => {
        await logout();
        navigate("/super-admin/login", { replace: true });
    };

    const NavLink = ({ to, icon: Icon, label, testid }) => {
        const active = location.pathname === to || (to === "/companies" && location.pathname === "/");
        return (
            <Link
                to={to}
                data-testid={testid}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.12em] rounded transition ${active ? "bg-white/15 text-white" : "text-white/60 hover:text-white hover:bg-white/10"}`}
            >
                <Icon className="w-3.5 h-3.5" /> {label}
            </Link>
        );
    };

    return (
        <div className="min-h-screen bg-bone" data-testid="super-admin-layout">
            <header className="sticky top-0 z-40 border-b border-ink/10" style={{ background: "linear-gradient(135deg, #0F1A2E 0%, #1F1F1F 100%)" }}>
                <div className="px-6 md:px-12 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3 text-white min-w-0">
                        <div className="w-9 h-9 rounded-md bg-white/10 backdrop-blur flex items-center justify-center">
                            <Shield className="w-4 h-4 text-white" strokeWidth={2} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">Super-admin platform</div>
                            <div className="font-display font-bold text-base">Suntosh HRM Operator Console</div>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-white/80 text-sm">
                        <div className="flex flex-wrap items-center gap-1 sm:mr-2 sm:border-r border-white/20 sm:pr-3">
                            <NavLink to="/companies" icon={Building2} label="Companies" testid="sa-nav-companies" />
                            <NavLink to="/super-admin/database" icon={Database} label="Database" testid="sa-nav-database" />
                            <NavLink to="/super-admin/billing" icon={CreditCard} label="Billing" testid="sa-nav-billing" />
                            <NavLink to="/super-admin/revenue" icon={TrendingUp} label="Revenue" testid="sa-nav-revenue" />
                            <NavLink to="/super-admin/support" icon={LifeBuoy} label={unread ? `Support (${unread})` : "Support"} testid="sa-nav-support" />
                        </div>
                        <ClockInReminderToggle className="!px-3 !py-1.5 !text-xs !rounded" labels={{ on: "Payment alerts on", off: "Payment alerts", enabled: "Payment alerts are on for this device", disabled: "Payment alerts turned off" }} />
                        <span className="hidden lg:inline">{user?.email}</span>
                        <button
                            onClick={() => setPwOpen(true)}
                            className="inline-flex items-center gap-2 border border-white/20 px-3 py-1.5 text-xs hover:bg-white/10 transition rounded"
                            data-testid="super-admin-change-pw"
                            title="Change your super-admin password"
                        >
                            <KeyRound className="w-3.5 h-3.5" /> Change password
                        </button>
                        <button onClick={onLogout} className="inline-flex items-center gap-2 border border-white/20 px-3 py-1.5 text-xs hover:bg-white/10 transition rounded" data-testid="super-admin-logout">
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            </header>
            <main>
                <Outlet />
            </main>
            {pwOpen && <PasswordChangeDialog onClose={() => setPwOpen(false)} />}
        </div>
    );
}

function PasswordChangeDialog({ onClose }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (next.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        if (next !== confirm) {
            toast.error("New passwords do not match");
            return;
        }
        if (next === current) {
            toast.error("New password must be different from your current password");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/super-admin/change-password", { current_password: current, new_password: next });
            toast.success("Password updated. Use the new password on your next sign-in.");
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="super-admin-pw-dialog">
            <div className="bg-white max-w-md w-full rounded-lg shadow-2xl">
                <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-klein" />
                        <h2 className="font-display font-bold text-lg">Change super-admin password</h2>
                    </div>
                    <button onClick={onClose} className="text-ink/40 hover:text-ink" data-testid="pw-dialog-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
                    <PwField label="Current password" value={current} onChange={setCurrent} show={showCurrent} setShow={setShowCurrent} testid="pw-current" />
                    <PwField label="New password" value={next} onChange={setNext} show={showNew} setShow={setShowNew} testid="pw-new" hint="At least 8 characters. Use a mix of letters, numbers and symbols." />
                    <PwField label="Confirm new password" value={confirm} onChange={setConfirm} show={showNew} setShow={setShowNew} testid="pw-confirm" />
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-ink/60 hover:text-ink" data-testid="pw-cancel">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || !current || !next || !confirm}
                            className="inline-flex items-center gap-1.5 bg-klein text-white px-4 py-2 text-sm rounded hover:bg-klein/90 disabled:opacity-50"
                            data-testid="pw-submit"
                        >
                            {submitting ? "Updating…" : "Update password"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function PwField({ label, value, onChange, show, setShow, hint, testid }) {
    return (
        <div>
            <label className="overline">{label}</label>
            <div className="mt-2 relative">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full border border-ink/15 px-3 py-2.5 pr-10 text-sm rounded focus:border-klein focus:outline-none"
                    data-testid={testid}
                    autoComplete="new-password"
                />
                <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink"
                    tabIndex={-1}
                >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
            </div>
            {hint && <p className="mt-1 text-[11px] text-ink/40">{hint}</p>}
        </div>
    );
}
